import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { API_URL } from '../utils/api';

// Convert VAPID public key (url-safe base64) to Uint8Array for PushManager.subscribe
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export default function PushSubscribeButton() {
  const [state, setState] = useState('idle'); // idle | checking | subscribed | unsupported | blocked | loading
  const [busy, setBusy] = useState(false);

  const supported = typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;

  const refresh = useCallback(async () => {
    if (!supported) { setState('unsupported'); return; }
    setState('checking');
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) { setState('idle'); return; }
      const sub = await reg.pushManager.getSubscription();
      if (Notification.permission === 'denied') {
        setState('blocked');
        return;
      }
      setState(sub ? 'subscribed' : 'idle');
    } catch {
      setState('idle');
    }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const subscribe = async () => {
    if (!supported) { toast.error('A böngésződ nem támogatja a push értesítést'); return; }
    setBusy(true);
    try {
      // Ensure SW is registered (registerSW only registers in prod; this also works runtime)
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        reg = await navigator.serviceWorker.register('/sw.js');
      }
      await navigator.serviceWorker.ready;

      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setState(perm === 'denied' ? 'blocked' : 'idle');
        toast.error('Értesítés engedély megtagadva');
        setBusy(false);
        return;
      }

      const keyResp = await axios.get(`${API_URL}/api/push/vapid-public-key`, { withCredentials: true });
      const applicationServerKey = urlBase64ToUint8Array(keyResp.data.public_key);

      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      const payload = sub.toJSON();
      await axios.post(`${API_URL}/api/push/subscribe`, payload, { withCredentials: true });

      setState('subscribed');
      toast.success('Push értesítések bekapcsolva');
    } catch (e) {
      toast.error('Feliratkozás sikertelen');
      // eslint-disable-next-line no-console
      console.warn('[push] subscribe failed', e);
    }
    setBusy(false);
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await axios.delete(`${API_URL}/api/push/subscribe`, {
          data: { endpoint: sub.endpoint },
          withCredentials: true,
        });
        await sub.unsubscribe();
      }
      setState('idle');
      toast.success('Push értesítések kikapcsolva');
    } catch {
      toast.error('Leiratkozás sikertelen');
    }
    setBusy(false);
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/push/test`, {}, { withCredentials: true });
      if (r.data?.sent > 0) toast.success('Teszt push elküldve'); else toast('Nincs aktív feliratkozás');
    } catch {
      toast.error('Teszt push hiba');
    }
    setBusy(false);
  };

  if (state === 'unsupported') return null;

  if (state === 'subscribed') {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={sendTest}
          disabled={busy}
          data-testid="push-test-btn"
          className="text-xs font-medium hover:underline disabled:opacity-50"
          style={{ color: 'var(--text-secondary)' }}
        >
          Teszt
        </button>
        <span style={{ color: 'var(--border)' }}>·</span>
        <button
          onClick={unsubscribe}
          disabled={busy}
          data-testid="push-unsubscribe-btn"
          className="text-xs font-medium hover:underline disabled:opacity-50 flex items-center gap-1"
          style={{ color: 'var(--text-secondary)' }}
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <BellOff size={11} />}
          Push ki
        </button>
      </div>
    );
  }

  if (state === 'blocked') {
    return (
      <span className="text-xs" data-testid="push-blocked" style={{ color: 'var(--text-muted)' }} title="A böngésződben engedélyezned kell az értesítést">
        Push letiltva
      </span>
    );
  }

  return (
    <button
      onClick={subscribe}
      disabled={busy}
      data-testid="push-subscribe-btn"
      className="text-xs font-medium hover:underline disabled:opacity-50 flex items-center gap-1"
      style={{ color: 'var(--accent-copper)' }}
    >
      {busy ? <Loader2 size={11} className="animate-spin" /> : <Bell size={11} />}
      Push bekapcsolása
    </button>
  );
}
